import React, { useMemo } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Alert, Paper, Stack, Typography } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferRatePlans } from '~/hooks/apis/reservation/useOfferRatePlans';
import { useGetOfferRoomTypes } from '~/hooks/apis/reservation/useOfferRoomTypes';

import { getOfferPropertyId } from '../../utils';

import RoomRateAvailabilityPanel from './RoomRateAvailabilityPanel';
import RoomTypesTable from './RoomTypesTable';

export default function RoomTypesTab() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const openRoomTypeId = searchParams.get('roomTypeId');
  const roomRateId = searchParams.get('roomRateId');
  const viewingRatePlanId = searchParams.get('ratePlanId');

  const { data: offer } = useGetOffer(offerId);
  const vendorId = offer?.vendor_account_id;
  const propertyId = getOfferPropertyId(offer);
  const { data: roomTypes = [], isLoading, error } = useGetOfferRoomTypes(propertyId, vendorId);
  const { data: property, isLoading: isLoadingProperty } = useGetProperty({ propertyId });

  const { data: ratePlans = [], isLoading: isLoadingRatePlans } = useGetOfferRatePlans(
    openRoomTypeId ? vendorId : undefined,
  );

  function handleOpenAvailability(roomTypeId: string) {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('roomTypeId', roomTypeId);
    nextSearchParams.delete('roomRateId');
    navigate({ search: nextSearchParams.toString() });
  }

  function handleChangeRoom(nextRoomTypeId: string) {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('roomTypeId', nextRoomTypeId);
    nextSearchParams.delete('roomRateId');
    navigate({ search: nextSearchParams.toString() });
  }

  function handleChangeRoomRate(nextRoomRateId: string | undefined) {
    const nextSearchParams = new URLSearchParams(location.search);
    if (nextRoomRateId) {
      nextSearchParams.set('roomRateId', nextRoomRateId);
    } else {
      nextSearchParams.delete('roomRateId');
    }
    navigate({ search: nextSearchParams.toString() }, { replace: true });
  }

  function handleViewRatePlan(ratePlanId: string) {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('ratePlanId', ratePlanId);
    navigate({ search: nextSearchParams.toString() });
  }

  function handleCloseRatePlan() {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete('ratePlanId');
    navigate({ search: nextSearchParams.toString() });
  }

  function handleClose() {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete('roomTypeId');
    nextSearchParams.delete('roomRateId');
    nextSearchParams.delete('ratePlanId');
    navigate({ search: nextSearchParams.toString() });
  }

  if (openRoomTypeId && (isLoadingProperty || isLoadingRatePlans)) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Spinner />
      </Paper>
    );
  }

  if (openRoomTypeId && property?.room_types && vendorId && propertyId) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <RoomRateAvailabilityPanel
          propertyId={propertyId}
          vendorId={vendorId}
          channelManaged={!!property.channel_managed}
          roomTypes={property.room_types}
          roomTypeId={openRoomTypeId}
          roomRateId={roomRateId}
          viewingRatePlanId={viewingRatePlanId}
          availableRatePlans={ratePlans}
          onChangeRoom={handleChangeRoom}
          onChangeRoomRate={handleChangeRoomRate}
          onViewRatePlan={handleViewRatePlan}
          onCloseRatePlan={handleCloseRatePlan}
          onClose={handleClose}
        />
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Stack spacing={0.5}>
          <Typography variant="h6">Room types</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
            Room types come from the property's room records. Open a room to manage its inventory, surcharges,
            blackouts, capacity and extra guest surcharges.
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          <Typography component="span" variant="subtitle2">
            {roomTypes.length}
          </Typography>{' '}
          room {roomTypes.length === 1 ? 'type' : 'types'}
        </Typography>
      </Stack>

      {error ? (
        <Alert severity="error">Unable to load room types.</Alert>
      ) : (
        <RoomTypesTable loading={isLoading} roomTypes={roomTypes} onOpenAvailability={handleOpenAvailability} />
      )}
    </Paper>
  );
}
